import { useEffect, useState } from "react";
import { Search, Loader2, X, Save, Phone, Mail, MessageSquare, Plus, BellRing, CalendarPlus } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import apiClient from "./api";
import ReminderModal from "./ReminderModal";
import NewBookingModal from "./NewBookingModal";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";

const STATUSES = ["new", "responded", "archived"];

const STATUS_COLOR = {
  new: "bg-[#FC5106]/15 text-[#FC5106] border-[#FC5106]/40",
  responded: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40",
  archived: "bg-white/10 text-white/60 border-white/20",
};

export default function AdminEnquiries() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sourceFilter, setSourceFilter] = useState("all"); // all | task | activation | manual
  const [drawer, setDrawer] = useState(null);
  const [reminderModal, setReminderModal] = useState(null); // {open, defaults}
  const [convertEnquiry, setConvertEnquiry] = useState(null); // enquiry to convert
  const [offerStats, setOfferStats] = useState(null);

  const redeem = async (e, m) => {
    e.stopPropagation();
    try {
      await apiClient.post(`/api/admin/contact/${m.id}/redeem`);
      setItems((list) => list.map((x) => (x.id === m.id ? { ...x, offer_redeemed: true } : x)));
      setOfferStats((st) => st && { ...st, redeemed: st.redeemed + 1, offers: st.offers.map((o) => (o.id === m.source_id ? { ...o, redeemed: o.redeemed + 1 } : o)) });
      toast.success(`${m.offer_code} marked as redeemed`);
    } catch {
      toast.error("Could not mark as redeemed");
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter !== "all") params.status = statusFilter;
      if (search) params.search = search;
      const { data } = await apiClient.get("/api/admin/contact", { params });
      setItems(data);
      apiClient.get("/api/admin/offers/stats").then((r) => setOfferStats(r.data)).catch(() => {});
    } catch {
      toast.error("Could not load enquiries");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, statusFilter]);

  const filteredItems = items.filter((m) => {
    if (sourceFilter === "manual") return !m.source_type;
    if (sourceFilter !== "all") return m.source_type === sourceFilter;
    return true;
  });

  const counts = {
    total: items.length,
    task: items.filter((m) => m.source_type === "task").length,
    activation: items.filter((m) => m.source_type === "activation").length,
    offer: items.filter((m) => m.source_type === "offer").length,
    manual: items.filter((m) => !m.source_type).length,
  };

  return (
    <div className="space-y-6" data-testid="admin-enquiries">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-[#C9BCFF] uppercase tracking-[0.25em] text-xs mb-2">Enquiries</p>
          <h1 className="font-heading text-3xl md:text-4xl font-bold text-white">
            All leads &amp; messages
          </h1>
          <p className="text-white/50 text-sm mt-1">
            <span className="text-white font-semibold">{items.length}</span> total ·{" "}
            <span className="text-[#C9BCFF]">{counts.task}</span> from calling tasks ·{" "}
            <span className="text-[#FC5106]">{counts.activation}</span> from activations ·{" "}
            <span className="text-[#FFB547]">{counts.offer}</span> offer claims ·{" "}
            <span className="text-white/60">{counts.manual}</span> from website form
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Link
            to="/admin/reminders"
            data-testid="enquiries-view-reminders"
            className="btn-secondary !py-2.5 !px-4 !text-sm"
          >
            <BellRing className="w-4 h-4" /> View reminders
          </Link>
          <button
            onClick={() => setReminderModal({ open: true, defaults: {} })}
            data-testid="enquiries-add-reminder"
            className="btn-primary !py-2.5 !px-4 !text-sm"
          >
            <Plus className="w-4 h-4" /> Add reminder
          </button>
        </div>
      </div>

      <div className="rounded-2xl border border-white/10 bg-[#121212] p-4 grid md:grid-cols-12 gap-3">
        <div className="md:col-span-9 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
          <input
            data-testid="enquiries-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name, phone, offer code, message…"
            className="w-full bg-black/50 border border-white/10 rounded-xl pl-10 pr-3 py-2.5 text-sm text-white placeholder-white/40 focus:border-[#FC5106] focus:ring-1 focus:ring-[#FC5106] outline-none"
          />
        </div>
        <div className="md:col-span-3">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger data-testid="enquiries-status-filter" className="bg-black/50 border-white/10 text-sm text-white">
              <SelectValue placeholder="All statuses" />
            </SelectTrigger>
            <SelectContent className="bg-[#121212] border-white/10 text-white">
              <SelectItem value="all">All statuses</SelectItem>
              {STATUSES.map((s) => (
                <SelectItem key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="md:col-span-12 flex flex-wrap gap-2">
          {[
            { v: "all", label: `All (${counts.total})` },
            { v: "task", label: `📞 Calling tasks (${counts.task})` },
            { v: "activation", label: `🏢 Activations (${counts.activation})` },
            { v: "offer", label: `🎁 Offer claims (${counts.offer})` },
            { v: "manual", label: `🌐 Website form (${counts.manual})` },
          ].map((f) => (
            <button
              key={f.v}
              onClick={() => setSourceFilter(f.v)}
              data-testid={`enquiries-source-${f.v}`}
              className={`text-xs uppercase tracking-wider px-3 py-1.5 rounded-full border transition-colors ${
                sourceFilter === f.v
                  ? "bg-[#FC5106] border-[#FC5106] text-white"
                  : "bg-black/30 border-white/10 text-white/70 hover:border-white/20"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {offerStats?.claimed > 0 && <OfferStats stats={offerStats} />}

      <div className="rounded-2xl border border-white/10 bg-[#121212]">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-6 h-6 text-[#FC5106] animate-spin" />
          </div>
        ) : filteredItems.length === 0 ? (
          <p className="text-white/40 text-center py-16 text-sm">
            {items.length === 0
              ? "No enquiries yet."
              : `No ${sourceFilter !== "all" ? sourceFilter + " " : ""}enquiries match these filters. Try clearing them.`}
          </p>
        ) : (
          <ul className="divide-y divide-white/5">
            {filteredItems.map((m) => (
              <li
                key={m.id}
                onClick={() => setDrawer(m)}
                className="px-5 py-4 hover:bg-white/[0.02] cursor-pointer"
                data-testid={`enquiry-row-${m.id}`}
              >
                <div className="flex items-start justify-between gap-3 mb-1">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-white font-semibold">{m.name}</p>
                      {m.source_type === "task" && (
                        <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#8B73FF]/15 text-[#C9BCFF] border border-[#8B73FF]/40">
                          Calling task
                        </span>
                      )}
                      {m.source_type === "activation" && (
                        <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#FC5106]/15 text-[#FC5106] border border-[#FC5106]/40">
                          Activation
                        </span>
                      )}
                      {m.offer_code && (
                        <span data-testid={`enquiry-offer-code-${m.id}`} className="text-[10px] font-mono font-bold tracking-wider px-2 py-0.5 rounded-full bg-[#FFB547]/15 text-[#FFB547] border border-[#FFB547]/40">
                          {m.offer_code}
                        </span>
                      )}
                      {m.offer_code && (m.offer_redeemed ? (
                        <span data-testid={`enquiry-redeemed-${m.id}`} className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-green-500/15 text-green-400 border border-green-500/40">
                          Redeemed
                        </span>
                      ) : offerExpiry(m) < new Date() ? (
                        <span data-testid={`enquiry-expired-${m.id}`} className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-white/5 text-white/50 border border-white/15">
                          Expired {fmtDay(offerExpiry(m))}
                        </span>
                      ) : (
                        <button onClick={(e) => redeem(e, m)} data-testid={`enquiry-redeem-btn-${m.id}`} className="text-[10px] uppercase tracking-wider font-semibold px-2.5 py-0.5 rounded-full bg-[#FC5106] text-white hover:bg-[#E24804] transition-colors">
                          Mark redeemed
                        </button>
                      ))}
                      {m.offer_code && !m.offer_redeemed && offerExpiry(m) >= new Date() && (
                        <span data-testid={`enquiry-expires-${m.id}`} className="text-[10px] text-white/50">Valid till {fmtDay(offerExpiry(m))}</span>
                      )}
                    </div>
                    <p className="text-white/50 text-xs">
                      {m.phone || m.email || "—"}
                      {m.created_by && <span className="ml-2 text-white/50">· by {m.created_by}</span>}
                    </p>
                  </div>
                  <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border whitespace-nowrap ${STATUS_COLOR[m.status]}`}>
                    {m.status}
                  </span>
                </div>
                <p className="text-white/70 text-sm line-clamp-2">{m.message}</p>
              </li>
            ))}
          </ul>
        )}
      </div>

      {drawer && (
        <EnquiryDrawer
          message={drawer}
          onClose={() => setDrawer(null)}
          onSaved={(updated) => {
            setItems((arr) => arr.map((m) => (m.id === updated.id ? updated : m)));
            setDrawer(updated);
          }}
          onAddReminder={(m) =>
            setReminderModal({
              open: true,
              defaults: {
                customer_name: m.name,
                customer_phone: m.phone,
                customer_email: m.email,
                notes: m.message ? `From enquiry: ${m.message}` : "",
              },
            })
          }
          onConvertToBooking={(m) => {
            setConvertEnquiry(m);
            setDrawer(null);
          }}
        />
      )}

      {convertEnquiry && (
        <NewBookingModal
          open
          defaults={{
            name: convertEnquiry.name === "Lead" ? "" : convertEnquiry.name,
            phone: convertEnquiry.phone || "",
            email: convertEnquiry.email || "",
            offer_code: convertEnquiry.offer_code && !convertEnquiry.offer_redeemed ? convertEnquiry.offer_code : "",
            message: convertEnquiry.subject ? `Converted from enquiry: ${convertEnquiry.subject}` : "",
          }}
          onClose={() => setConvertEnquiry(null)}
          onCreated={async (booking) => {
            try {
              const { data } = await apiClient.patch(`/api/admin/contact/${convertEnquiry.id}`, {
                status: "responded",
                internal_notes: `Converted to booking ${booking.id} on ${new Date().toISOString().slice(0, 10)}`,
              });
              setItems((arr) => arr.map((m) => (m.id === data.id ? data : m)));
              apiClient.get("/api/admin/offers/stats").then((r) => setOfferStats(r.data)).catch(() => {});
              toast.success("Booking created · enquiry marked Responded");
            } catch {
              toast.success("Booking created");
            }
            setConvertEnquiry(null);
          }}
        />
      )}

      {reminderModal?.open && (
        <ReminderModal
          open
          defaults={reminderModal.defaults}
          onClose={() => setReminderModal(null)}
          onCreated={() => toast.success("Reminder added — view it in Reminders tab")}
        />
      )}
    </div>
  );
}

function EnquiryDrawer({ message, onClose, onSaved, onAddReminder, onConvertToBooking }) {
  const [status, setStatus] = useState(message.status);
  const [notes, setNotes] = useState(message.internal_notes || "");
  const [saving, setSaving] = useState(false);

  const dirty = status !== message.status || (notes || "") !== (message.internal_notes || "");

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await apiClient.patch(`/api/admin/contact/${message.id}`, {
        status, internal_notes: notes,
      });
      toast.success("Saved");
      onSaved(data);
    } catch {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const waUrl = message.phone
    ? `https://wa.me/${(message.phone || "").replace(/\D/g, "").length === 10 ? "91" + message.phone.replace(/\D/g, "") : message.phone.replace(/\D/g, "")}`
    : null;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex justify-end"
      onClick={onClose}
      data-testid="enquiry-drawer"
    >
      <div
        className="w-full max-w-lg h-full bg-[#0d0d0d] border-l border-white/10 overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-[#0d0d0d]/95 backdrop-blur border-b border-white/10 px-6 py-4 flex items-center justify-between">
          <div>
            <p className="text-[10px] uppercase tracking-wider text-white/50">Enquiry</p>
            <h2 className="font-heading text-lg font-bold text-white truncate max-w-[260px]">{message.name}</h2>
          </div>
          <button onClick={onClose} data-testid="enquiry-drawer-close" className="p-2 rounded-full bg-white/5 hover:bg-white/10 text-white">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-6 space-y-5">
          {message.email && (
            <div>
              <p className="text-xs uppercase tracking-wider text-white/50 mb-1 flex items-center gap-1.5">
                <Mail className="w-3 h-3" /> Email
              </p>
              <a href={`mailto:${message.email}`} className="text-[#C9BCFF] hover:underline">{message.email}</a>
            </div>
          )}
          {message.phone && (
            <div>
              <p className="text-xs uppercase tracking-wider text-white/50 mb-1 flex items-center gap-1.5">
                <Phone className="w-3 h-3" /> Phone
              </p>
              <a href={`tel:${message.phone}`} className="text-[#C9BCFF] hover:underline">{message.phone}</a>
            </div>
          )}
          {message.subject && (
            <div>
              <p className="text-xs uppercase tracking-wider text-white/50 mb-1">Subject</p>
              <p className="text-white/80 text-sm">{message.subject}</p>
            </div>
          )}
          <div>
            <p className="text-xs uppercase tracking-wider text-white/50 mb-1">Message</p>
            <p className="text-white/80 text-sm whitespace-pre-line bg-black/40 rounded-xl p-4 border border-white/10">{message.message}</p>
          </div>

          <div className="pt-2 border-t border-white/10" />

          <div>
            <label className="text-xs uppercase tracking-wider text-white/50 mb-2 block">Status</label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger data-testid="enquiry-status-select" className="bg-black/50 border-white/10 text-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#121212] border-white/10 text-white">
                {STATUSES.map((s) => (
                  <SelectItem key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className="text-xs uppercase tracking-wider text-white/50 mb-2 block">Internal notes</label>
            <textarea
              data-testid="enquiry-notes"
              rows={4}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Private notes only visible to staff."
              className="w-full bg-black/50 border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white placeholder-white/40 focus:border-[#FC5106] focus:ring-1 focus:ring-[#FC5106] outline-none resize-none"
            />
          </div>

          <div className="flex flex-wrap gap-2">
            {message.email && (
              <a href={`mailto:${message.email}`} className="btn-secondary !py-2.5 !px-4 !text-sm">
                <Mail className="w-4 h-4" /> Reply by email
              </a>
            )}
            {waUrl && (
              <a
                href={waUrl}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center justify-center gap-2 bg-[#25D366]/15 border border-[#25D366]/40 text-[#25D366] hover:bg-[#25D366]/25 rounded-full px-4 py-2.5 text-sm font-semibold"
              >
                <MessageSquare className="w-4 h-4" /> WhatsApp
              </a>
            )}
            <button
              onClick={() => {
                onAddReminder && onAddReminder(message);
                onClose();
              }}
              data-testid="enquiry-add-reminder"
              className="inline-flex items-center justify-center gap-2 bg-[#FC5106]/15 border border-[#FC5106]/40 text-[#FC5106] hover:bg-[#FC5106]/25 rounded-full px-4 py-2.5 text-sm font-semibold"
            >
              <BellRing className="w-4 h-4" /> Set reminder
            </button>
            {message.phone && (
              <button
                onClick={() => onConvertToBooking && onConvertToBooking(message)}
                data-testid="enquiry-convert-booking"
                className="inline-flex items-center justify-center gap-2 bg-emerald-500/15 border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/25 rounded-full px-4 py-2.5 text-sm font-semibold"
              >
                <CalendarPlus className="w-4 h-4" /> Convert to booking
              </button>
            )}
          </div>

          <button
            onClick={save}
            disabled={!dirty || saving}
            data-testid="enquiry-save"
            className="btn-primary w-full justify-center disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving…</> : <><Save className="w-4 h-4" /> Save changes</>}
          </button>
        </div>
      </div>
    </div>
  );
}

function OfferStats({ stats }) {
  const pct = (r, c) => (c ? Math.round((r / c) * 100) : 0);
  return (
    <div data-testid="offer-stats" className="rounded-2xl border border-white/10 bg-[#121212] p-5">
      <div className="flex flex-wrap items-end gap-x-8 gap-y-3 mb-4">
        <div>
          <p className="text-white/50 text-xs uppercase tracking-wider">Offer codes claimed</p>
          <p data-testid="offer-stats-claimed" className="font-heading text-3xl font-bold text-white">{stats.claimed}</p>
        </div>
        <div>
          <p className="text-white/50 text-xs uppercase tracking-wider">Redeemed</p>
          <p data-testid="offer-stats-redeemed" className="font-heading text-3xl font-bold text-green-400">{stats.redeemed}</p>
        </div>
        <div>
          <p className="text-white/50 text-xs uppercase tracking-wider">Expired</p>
          <p data-testid="offer-stats-expired" className="font-heading text-3xl font-bold text-white/60">{stats.expired || 0}</p>
        </div>
        <div>
          <p className="text-white/50 text-xs uppercase tracking-wider">Converted</p>
          <p data-testid="offer-stats-rate" className="font-heading text-3xl font-bold text-[#FC5106]">{pct(stats.redeemed, stats.claimed)}%</p>
        </div>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {stats.offers.map((o) => (
          <div key={o.id} data-testid={`offer-stats-${o.id}`} className="rounded-xl border border-white/10 bg-black/30 p-3">
            <p className="text-white/80 text-xs line-clamp-2 min-h-[2rem]">{o.title}</p>
            <p className="mt-2 text-sm text-white"><span className="font-bold">{o.redeemed}</span><span className="text-white/50"> / {o.claimed} redeemed · {pct(o.redeemed, o.claimed)}%</span></p>
            <div className="mt-2 h-1.5 rounded-full bg-white/10 overflow-hidden">
              <div className="h-full bg-[#FC5106]" style={{ width: `${pct(o.redeemed, o.claimed)}%` }} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const OFFER_VALID_DAYS = 30;
const offerExpiry = (m) => new Date(m.offer_expires_at || new Date(new Date(m.created_at).getTime() + OFFER_VALID_DAYS * 864e5));
const fmtDay = (d) => d.toLocaleDateString("en-IN", { day: "numeric", month: "short" });
